import React, { useEffect, useState } from "react"
import { Accordion, Col, Modal, Row, Tab, Tabs } from "react-bootstrap"
import API from "@/common/API"
import Settings from "@/common/Settings"
import { parsedUser } from "@/common/GetCurrentUser"
import { userAvatar } from "@/common/helpers"
import { useHistory } from "react-router-dom"
import { Routes } from "@/routes"
import { useTranslation } from "react-i18next"

const getPlaceImageUrl = image => {
  if (!image) return null
  const base = Settings.BasePathForAPI || Settings.BasePath
  return `${base}file/getThumbnail?filename=${encodeURIComponent(image)}`
}

const formatAddress = item => {
  if (!item) return ""
  return [item.address, item.sector, item.city, item.region, item.country]
    .filter(Boolean)
    .join(", ")
}

const PlacePriority = props => {
  const [activeSheet, setActiveSheet] = useState("misLugares")
  const { t } = useTranslation("global")
  const user = parsedUser()
  const history = useHistory()
  const [recordsByMe, setRecordsByMe] = useState([])
  const [records, setRecords] = useState([])

  const requestData = async () => {
    if (!user?.ID) return
    const query = "&accountId=" + user.ID
    const requestAPI = await API.getAction("place/priorityList?" + query)
    if (requestAPI.data.status === "ok") {
      setRecords(requestAPI.data.response || [])
    }
  }

  const requestByMeData = async () => {
    if (!user?.ID) return
    const query = "&accountId=" + user.ID
    const requestAPI = await API.getAction("place/PlacePriorityAssignedByMe?" + query)
    if (requestAPI.data.status === "ok") {
      setRecordsByMe(requestAPI.data.response || [])
    }
  }

  useEffect(() => {
    requestData()
  }, [])

  useEffect(() => {
    if (activeSheet === "misLugares") {
      requestData()
    } else {
      requestByMeData()
    }
  }, [activeSheet])

  const renderPlaceCard = (item, key) => {
    if (!item?.id) return null
    const placeImage = getPlaceImageUrl(item.image)
    const placeName = item.name || t("Unnamed place")
    const addressText = formatAddress(item)
    const assignedName = item.assignedTo
      ? `${item.assignedTo.firstName || ""} ${item.assignedTo.lastName || ""}`.trim()
      : t("Unassigned")

    return (
      <div className="card shadow-sm my-3 mx-2" key={key}>
        <div
          className="card-body p-2 clickable"
          onClick={() => history.push(Routes.PlaceDetails.clean + item.id)}
        >
          <div className="row mx-0 align-items-start">
            <div className="col-3">
              <img
                className="img-fluid rounded"
                style={{ height: 100, width: 100, objectFit: "cover" }}
                src={placeImage || userAvatar(placeName, 100)}
                alt={placeName}
                onError={event => {
                  event.currentTarget.src = userAvatar(placeName, 100)
                }}
              />
            </div>
            <div className="col-9">
              <div className="row">
                <div className="col">
                  <b className="text-break">{placeName}</b>
                </div>
                <div className="col text-end">
                  {item?.stage?.icon && <i className={item.stage.icon} />}
                  <span className={"badge ms-2 py-1 " + (item.visits === 0 ? "bg-danger" : "bg-info")}>
                    {t("Visits")}: {item.visits || 0}
                  </span>
                </div>
              </div>
              <p className="card-text text-wrap mb-1">
                {addressText || t("No address available")}
              </p>
              <p className="mb-0">
                <span>{t("Assigned to")}: {assignedName}</span>
              </p>
            </div>
          </div>
        </div>

        <div className="card-footer">
          <button
            className="btn btn-success btn-sm"
            type="button"
            onClick={() => {
              const fullAddress = `${item.latitude},${item.longitude}`
              window.open(
                `https://www.google.com/maps/search/?api=1&query=${fullAddress}`,
                "MapWindow",
                "width=1280,height=840"
              )
            }}
          >
            {t("Get Directions")}
          </button>
        </div>
      </div>
    )
  }

  return (
    <Modal show={props.show} onHide={props.toggle} size="lg" className="modalRight">
      <Modal.Header closeButton>
        <Row className="mx-0 w-100">
          <Col>
            <h5>{t("Priority")}</h5>
          </Col>
        </Row>
      </Modal.Header>

      <Modal.Body className="p-0" style={{ overflow: "auto" }}>
        <Tabs
          defaultActiveKey={activeSheet}
          id="priority-tabs"
          className="mb-3"
          activeKey={activeSheet}
          onSelect={key => setActiveSheet(key)}
        >
          <Tab eventKey="misLugares" title={t("My places")}>
            {records.length === 0 ? (
              <div className="text-center text-muted py-4">{t("No priority places found")}</div>
            ) : (
              records.map((record, index) => renderPlaceCard(record?.place, `mine-${index}`))
            )}
          </Tab>

          <Tab eventKey="profile" title={t("Assigned by me")}>
            <Accordion defaultActiveKey="0">
              {recordsByMe.length === 0 && (
                <div className="text-center text-muted py-4">{t("No assignments found")}</div>
              )}
              {recordsByMe.map((record, index) => (
                <Accordion.Item eventKey={String(index)} key={index}>
                  <Accordion.Header>
                    {record?.account?.firstName} {record?.account?.lastName} ({record?.places?.length || 0})
                  </Accordion.Header>
                  <Accordion.Body>
                    {record?.places?.length > 0 ? (
                      record.places.map((item, placeIndex) =>
                        renderPlaceCard(item, `assigned-${index}-${placeIndex}`)
                      )
                    ) : (
                      <div className="text-muted">{t("No places in this assignment")}</div>
                    )}
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          </Tab>
        </Tabs>
      </Modal.Body>
    </Modal>
  )
}

export default PlacePriority
